import React, { lazy, Suspense, useContext, useEffect, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Box, Button, Grid, IconButton } from '@mui/material'
import { GridToolbarContainer } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'

import UserAPI from '../../api/orbitV1/UserAPI'
import { Settings } from '../../assets/icons/circles/Settings'
import BreadcrumbsMUI from '../../common/AbstractBreadcrumbs/BreadcrumbsMUI'
import MUIDataGrid from '../../common/AbstractDataGrid/DataGridMUI'
import PageLayout from '../../common/Layout/Page/PageLayout'
import LayoutSkeleton from '../../common/Skeletons/LayoutSkeleton'
import TableSkeleton from '../../common/Skeletons/TableSkeleton'
import chips from '../../common/ui/chips'
import { ColorBoxContent } from '../../common/ui/colorBox'
import JoyrideContext from '../../context/joyride/joyrideContext'
import OrbitSession from '../../hooks/useCookies'
import useOrbitFlag from '../../hooks/useOrbitFlag'

const StatContainer = lazy(
  () => import('../../common/AbstractStatLine/StatContainer')
)
const CustomSearchbar = lazy(
  () => import('../../common/AbstractDataGrid/CustomSearchbar')
)

export default function AccountManagement() {
  const navigate = useNavigate()
  const orgId = OrbitSession.get('orgId')
  const userId = OrbitSession.get('_id')

  const [fetchedUserAccounts, setFetchedUserAccounts] = useState([])
  const [dataArray, setDataArray] = useState([])

  const FlagCounters = useOrbitFlag('manageAccounts-counters')
  const joyrideContext = useContext(JoyrideContext)
  const { tourActive, setRun } = joyrideContext

  const baseRoute = window.location.pathname.includes('/app/')
    ? '/app/manage-accounts'
    : '/hq/manage-accounts'

  const {
    status,
    data: userData,
    error,
    isStale,
    refetch
  } = useQuery(['getOrgDetails', 'accountManagement'], async () => {
    const { data } = await UserAPI.getUserByQuery({ orgId })
    return data
  })

  if (error) {
    toast.error('Error loading user data!')
  }

  useEffect(() => {
    if (status === 'success') {
      if (isStale) {
        refetch()
      }
      setFetchedUserAccounts(userData)
      setDataArray([
        { title: 'Total Users', info: userData.length },
        {
          title: 'Total Admins',
          info: userData.filter(
            (person) => person.role.toUpperCase() === 'ADMIN'
          ).length
        }
      ])
      setTimeout(() => {
        if (tourActive) {
          setRun(true)
        }
      }, 1000)
    }
  }, [userData, status])

  // DATA GRID COMPONENT
  const rows = fetchedUserAccounts.map((userAccount) => {
    return {
      name: userAccount.name,
      _id: userAccount._id,
      id: userAccount._id,
      email: userAccount.email,
      role: userAccount.role,
      actions: userAccount.actions
    }
  })

  const columns = [
    { field: 'name', headerName: 'Name', width: 150 },
    { field: 'email', headerName: 'Email', width: 150 },
    {
      field: 'role',
      headerName: 'Role',
      width: 150,
      renderCell: (params) => {
        const userAccount = params.row
        return chips.users[userAccount.role.toUpperCase()]
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params) => {
        const userAccount = params.row
        if (userId === userAccount._id) {
          return null
        }
        return (
          <IconButton
            as={RouterLink}
            to={`view?user=${userAccount._id}`}
            style={{ padding: 0, margin: 0 }}
            data-cy='actionBtn'
          >
            <Settings />
          </IconButton>
        )
      }
    }
  ]

  const AccountToolbar = () => {
    return (
      <GridToolbarContainer
        sx={{
          justifyContent: 'space-between'
        }}
      >
        <CustomSearchbar />

        <div id='manageAccounts__addUserBtn'>
          <Button
            variant='filled'
            size='small'
            onClick={() => navigate('add')}
            data-cy='addNewUserBtn'
          >
            Add New User
          </Button>
        </div>
      </GridToolbarContainer>
    )
  }

  return (
    <Suspense fallback={<LayoutSkeleton />}>
      <PageLayout header='Manage Accounts'>
        <Grid container direction='column' spacing={2}>
          <Grid item>
            <ColorBoxContent variant='white'>
              <BreadcrumbsMUI
                crumbs={[
                  {
                    route: baseRoute,
                    name: 'User Accounts'
                  }
                ]}
              />
            </ColorBoxContent>
          </Grid>

          {FlagCounters && (
            <Grid item>
              <StatContainer dataArray={dataArray} />
            </Grid>
          )}

          <Grid item>
            <ColorBoxContent>
              <Box>
                <div
                  id='manageAccounts__mainDiv'
                  style={{ height: '100%', bottom: 0 }}
                >
                  {status === 'loading' ? (
                    <TableSkeleton />
                  ) : (
                    <div id='manageAccounts_dataGrid'>
                      <MUIDataGrid
                        rows={rows}
                        columns={columns}
                        components={{ Toolbar: AccountToolbar }}
                      />
                    </div>
                  )}
                </div>
              </Box>
            </ColorBoxContent>
          </Grid>
        </Grid>
      </PageLayout>
    </Suspense>
  )
}
